import { type FormEvent, useEffect, useMemo } from "react";
import { FormProvider, type Resolver, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { defineStepper } from "@stepperize/react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { WFPButton } from "@/components/ui/button";

import Loading from "../../../../components/Loading";
import Module from "../../../../components/Module";
import { isEmpty } from "../../../../utils";
import { InfoHubContentTypeStep } from "./Components/InfoHubContentTypeStep";
import { InfoHubReviewStep } from "./Components/InfoHubReviewStep";
import { InfoHubMobilePreviewStep } from "./Components/InfoHubMobilePreviewStep";
import { InfoHubStepNavigation } from "./Components/InfoHubStepNavigation";
import { createInfoHubFormSchema } from "./schema";
import type { InfoHubFormProps, InfoHubFormValues } from "./types";

const infoHubStepper = defineStepper([
  { id: "details" },
  { id: "review" },
  { id: "mobilePreview" },
] as const);

const STEP_ORDER = infoHubStepper.steps.map(step => step.id);

export const Form = ({
  isFetching,
  errorMessage = "",
  onClickValidateAndClose,
  initialStep,
  initialValues = {},
  contentTypes = [],
  topics = [],
  aggregators = [],
  units = [],
  loading,
  isCopy = false,
}: InfoHubFormProps) => {
  const { t } = useTranslation();

  const schema = useMemo(
    () =>
      createInfoHubFormSchema({
        required: t("traderDetails.form.mandatoryFieldBlankError"),
        quantityPositive: t("infoHub.form.quantityPositiveError"),
      }),
    [t]
  );

  const methods = useForm<InfoHubFormValues>({
    defaultValues: initialValues,
    mode: "onSubmit",
    reValidateMode: "onChange",
    resolver: zodResolver(schema) as Resolver<InfoHubFormValues>,
  });
  const {
    handleSubmit,
    reset,
    trigger,
    register,
    setValue,
    formState: { errors },
  } = methods;

  const stepper = infoHubStepper.useStepper({
    defaultStep: STEP_ORDER[initialStep ?? 0] ?? "details",
  });

  // Preload values when editing/copying; otherwise just register the hidden
  // `status` field so it is part of the submitted payload.
  useEffect(() => {
    if (!isEmpty(initialValues)) {
      reset(initialValues);
    } else {
      register("status");
    }
  }, [initialValues]);

  const getStepHeading = (stepIndex: number, addDetails = false) => {
    switch (stepIndex) {
      case 0:
        return addDetails ? t("infoHub.heading.createDetails") : t("infoHub.heading.addDetails");
      case 1:
        return addDetails ? t("infoHub.heading.reviewDetails") : t("infoHub.heading.review");
      case 2:
        return t("infoHub.heading.mobilePreview");
      default:
        return "";
    }
  };

  const submit = handleSubmit(onClickValidateAndClose);

  const next = () => {
    if (stepper.isLast) {
      setValue("status", "published");
      void submit();
    } else {
      void trigger();
      void stepper.next();
    }
  };

  const previous = () => void stepper.prev();

  const saveAsDraft = () => {
    setValue("status", "draft");
    void submit();
  };

  const nextButtonLabel = () => (stepper.isLast ? t("infoHub.footer.publish") : t("common.next"));

  const hasErrors = Object.keys(errors).length > 0;

  const stepLabels = useMemo(() => STEP_ORDER.map((_, index) => getStepHeading(index)), [t]);

  if (isFetching) {
    return <Loading isVisible />;
  }

  return (
    <div className="mx-auto w-full max-w-6xl">
      <div className="flex flex-col gap-8 lg:flex-row lg:gap-12">
        <aside className="shrink-0 lg:w-64">
          {/* Center the step graph within the sidebar space on wide screens; stays
              left-aligned when the layout stacks on small screens. */}
          <div className="lg:mx-auto lg:w-fit">
            <InfoHubStepNavigation
              steps={stepLabels}
              activeStep={stepper.index}
              label={t("infoHub.form.pageTitle")}
              onSelect={index => void stepper.goTo(STEP_ORDER[index])}
            />
          </div>
        </aside>
        <div className="min-w-0 flex-1">
          <Module
            title={`${t("infoHub.form.step")} ${stepper.index + 1}: ${getStepHeading(
              stepper.index,
              true
            )}`}
            footer={
              <div className="flex w-full items-center justify-between">
                <div>
                  {!stepper.isFirst && (
                    <WFPButton type="button" variant="outline" onClick={previous}>
                      <ArrowLeft />
                      {t("infoHub.footer.previous")}
                    </WFPButton>
                  )}
                </div>
                <div className="flex gap-3">
                  {stepper.isLast && (
                    <WFPButton type="button" variant="secondary" onClick={saveAsDraft}>
                      {t("infoHub.footer.saveAsDraft")}
                    </WFPButton>
                  )}
                  <WFPButton
                    type="button"
                    variant={stepper.isLast ? "default" : "secondary"}
                    disabled={hasErrors && stepper.isLast}
                    onClick={next}
                  >
                    {nextButtonLabel()}
                    <ArrowRight />
                  </WFPButton>
                </div>
              </div>
            }
          >
            <FormProvider {...methods}>
              <form onSubmit={(event: FormEvent<HTMLFormElement>) => event.preventDefault()}>
                {errorMessage ? (
                  <Alert variant="destructive" className="mb-4">
                    <AlertDescription>{errorMessage}</AlertDescription>
                  </Alert>
                ) : null}
                {stepper.is("details") && (
                  <InfoHubContentTypeStep
                    methods={methods}
                    contentTypes={contentTypes}
                    aggregators={aggregators}
                    topics={topics}
                    units={units}
                    loading={loading}
                    isCopy={isCopy}
                  />
                )}
                {stepper.is("review") && <InfoHubReviewStep methods={methods} />}
                {stepper.is("mobilePreview") && <InfoHubMobilePreviewStep methods={methods} />}
              </form>
            </FormProvider>
          </Module>
        </div>
      </div>
    </div>
  );
};
